import React, { Component } from "react"
import PropTypes from "prop-types"
import isEmpty from "lodash/isEmpty"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import { DEFAULT_THRESHOLD_NORMAL } from "constants/layout"
import { withRadium, gtmEvent as withGtmEvent } from "higher-order-components"
import { Link } from "react-router-dom"
import { ExpandableInfo } from "containers"
import { actionCreators, selectors } from "store/actions/mymazda"
import VehicleServiceDetails from "./details"
import gtmHandler from "AtomicUtils/gtmHandler"
import classnames from "classnames"
import { H2, Interactive, Space, FormGroup, Caption, InputSelect } from "AtomicComponents"
import buttonStyles from "AtomicComponents/Button/Button.less"

const scheduledOnly = s =>
  s.factoryCodeEntity.type === "Scheduled Maintenance" && s.factoryCodeEntity.distance > 0

class VehicleServiceSummary extends Component {
  constructor(props) {
    super(props)

    if (isEmpty(props.service)) {
      this.props.updateServiceDetails(
        {
          activeServiceCodeIndex: 0,
          selectedAdditionalServices: {}
        },
        this.props.vin
      )
    }

    this.activeFactoryCode = () => {
      const { service, serviceItems, factoryCode } = this.props
      const activeServiceCodeIndex = (service && service.activeServiceCodeIndex !== -1) ? service.activeServiceCodeIndex : 0

      return service
        ? serviceItems.serviceItems[activeServiceCodeIndex].factoryCode
        : factoryCode || null
    }

    this.activeServiceCodeIndex = factoryCode => {
      return this.props.serviceItems.serviceItems.filter(scheduledOnly).findIndex(item => item.factoryCode === factoryCode)
    }

    this.setService = s => {
      const { service } = this.props

      if (service && service.activeServiceCodeIndex === this.activeServiceCodeIndex(s)) {
        return
      }

      this.props.updateServiceDetails({
        activeServiceCodeIndex: this.activeServiceCodeIndex(s),
        selectedAdditionalServices: {}
      }, this.props.vin)
    }
  }

  renderServiceSelect() {
    const { serviceItems, service } = this.props
    const options = serviceItems.serviceItems.filter(scheduledOnly).map((s, index) => ({
      name: s.factoryCodeEntity.description,
      code: s.factoryCodeEntity.code,
      label: s.factoryCodeEntity.description,
      value: s.factoryCodeEntity.code,
      index
    }))
    const activeServiceIndex = options.findIndex(s => s.code === this.activeFactoryCode())

    return (
      <InputSelect
        label="Please Select"
        defaultValue={options[activeServiceIndex]}
        options={options}
        onChange={this.setService} />
    )
  }

  isSPG() {
    const { serviceItems } = this.props
    try {
      // trying to guess MSS vs SPG service type
      // MSS factoryCode starts with 'S' and SPG starts with 'M' (yes, it's counterintuitive)
      return serviceItems && serviceItems.serviceItems.some(si => si.factoryCode[0] === "M")
    } catch (e) {
      return false
    }
  }

  render() {
    const {
      title,
      termsAndConditions,
      serviceItems,
      expandDetails,
      nameplate,
      grade,
      rego,
      vin,
      gtmEvent,
      gtmOnClickContinue,
      serviceBookingUrl
    } = this.props

    const continueButtonText = "Continue"

    const { errorMessage } = this.state
    const renderSelect =
      serviceItems &&
      serviceItems.serviceItems &&
      serviceItems.serviceItems.length > 0 &&
      !errorMessage
    const showDetails = serviceItems && !errorMessage && this.activeFactoryCode()
    const mssDisclaimer =
      serviceItems && !this.isSPG()
        ? "The total service price is the Base Scheduled Maintenance, plus any Additional Scheduled Maintenance items that are due."
        : null

    return (
      <div style={{ position: "relative", width: "100%" }}>
        <H2 withDetails>{title}</H2>
        <Space />
        {
          serviceItems &&
          serviceItems.vehicle && (
            <Interactive>
              {serviceItems.vehicle.modelGroup || nameplate} {serviceItems.vehicle.grade || grade}{" "}
              | {serviceItems.vehicle.rego || rego}
            </Interactive>
          )
        }
        <Space />
        <FormGroup>
          {renderSelect && this.renderServiceSelect()}
        </FormGroup>
        {errorMessage && (
          <div data-test="error-message" style={styles.error}>
            {errorMessage}
          </div>
        )}
        {showDetails && (
          <div>
            <div style={{ padding: "1em" }}>
              <VehicleServiceDetails
                data={serviceItems}
                factoryCode={this.activeFactoryCode()}
                selectAdditionalItems
                expandDetails={expandDetails}
                vin={vin} />
              {mssDisclaimer && <p style={[styles.textLeft]}>{mssDisclaimer}</p>}
              {termsAndConditions && (
                <div style={{ textAlign: "left" }}>
                  <Caption>
                    *Additional maintenance may be required based on how you use your vehicle and
                    may incur additional cost.<br />This can be discussed with your service dealer.
                  </Caption>
                  <Space />
                  <ExpandableInfo
                    color="rgb(170, 170, 170)"
                    mainText="Read full terms and conditions"
                    description={termsAndConditions}
                    textAlign="left"
                    styleName="black" />
                </div>
              )}
            </div>
            {expandDetails && (
              <div
                onClick={() => {
                  gtmHandler({
                    pageCategory: "MyMazda Servicing",
                    pageAction: `Calculator - Clicked  "${continueButtonText.toUpperCase()}"`
                  })
                }}
                style={styles.mainCTAButtons}>
                <Link
                  to={serviceBookingUrl}
                  className={`Button ${classnames({
                    [buttonStyles.Button]: true,
                    [buttonStyles.ButtonRed]: true
                  })}`}>
                  {continueButtonText}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
}

const breakpoint1 = `@media (min-width: ${DEFAULT_THRESHOLD_NORMAL}px)`

const styles = {
  textLeft: {
    textAlign: "left",
    fontWeight: 200,
    padding: ".6em 1em 1em 0",
    lineHeight: "23px"
  },
  error: {
    textAlign: "center",
    fontWeight: 200,
    color: "#910A2D",
    padding: "1em",
    margin: "1em auto",
    lineHeight: 1.8
  },
  mainCTAButtons: {
    boxSizing: "border-box",
    display: "flex",
    backgroundColor: "white",
    justifyContent: "left",
    padding: "0.5em",
    flexDirection: "column",
    [breakpoint1]: {
      flexDirection: "row"
    }
  },
  regoInput: {
    width: "100%",
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    [breakpoint1]: {
      flexDirection: "row",
      maxWidth: "30em",
      alignItems: "center"
    }
  },
  popup: {
    position: "fixed",
    left: "0px",
    right: "0px",
    bottom: "0px",
    top: "0px",
    padding: "2em",
    backgroundColor: "#e5e6e9",
    zIndex: "100"
  },
  changeVinRego: {
    fontWeight: 600,
    letterSpacing: 1.8,
    color: "#aaaaaa",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: "81%",
    textTransform: "uppercase",
    padding: "1em 1em 3em"
  },
  changeVinRegoDesktop: {
    [breakpoint1]: {
      display: "block"
    },
    display: "none"
  },
  changeVinRegoMobile: {
    [breakpoint1]: {
      display: "none"
    },
    display: "block"
  }
}

VehicleServiceSummary.propTypes = {
  title: PropTypes.string,
  serviceItems: PropTypes.object,
  termsAndConditions: PropTypes.string,
  serviceBookingUrl: PropTypes.string,
  expandDetails: PropTypes.bool,
  factoryCode: PropTypes.string,
  service: PropTypes.object,
  nameplate: PropTypes.string,
  grade: PropTypes.string,
  rego: PropTypes.string,
  vin: PropTypes.string,
  updateServiceDetails: PropTypes.func,
  gtmEvent: PropTypes.func,
  gtmOnClickContinue: PropTypes.func
}

const mapStateToProps = (state, props) => ({
  service: selectors.getService(props.vin)(state)
})

const mapDispatchToProps = dispatch => bindActionCreators(actionCreators, dispatch)

export default withRadium(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withGtmEvent()
)(VehicleServiceSummary)



// WEBPACK FOOTER //
// ./src/components/mymazda/service/summary.jsx